import { mapActions, mapState, mapGetters } from 'vuex';
import purchaseOfferSushi from '@/components/purchase/sushi/purchase-offer-sushi/purchase-offer-sushi.vue';
import purchaseOfferDesktopSushi from '@/components/purchase/sushi/purchase-offer-desktop-sushi/purchase-offer-desktop-sushi.vue';
import purchaseOfferDesktopSushiCommonBetslip from '@/components/purchase/sushi/purchase-offer-desktop-sushi-common-betslip/purchase-offer-desktop-sushi-common-betslip.vue';
import purchaseOfferSushiMobileBar from '@/components/purchase/sushi/purchase-offer-sushi-mobile-bar/purchase-offer-sushi-mobile-bar.vue';
import loaderMixin from '@/mixins/loader';
import accountVerifyMixin from '@/mixins/accountVerify';
import notificationProxy from '@/services/notifications/proxy';
import { MODALS } from '@/components/app/app-modals/app-modals';
import { get } from 'lodash';
import Contract from '@/models/contract';
import { OPERATIONS, VALUES, getActiveButtonAfterSwitch } from '@/components/market/market-contract-horizontal-sushi/market-contract-horizontal-sushi.js';
import log from '@/logger';

export default {
  mixins: [
    accountVerifyMixin,
    loaderMixin,
  ],
  components: {
    purchaseOfferSushi,
    purchaseOfferSushiMobileBar,
    purchaseOfferDesktopSushi,
    purchaseOfferDesktopSushiCommonBetslip,
  },
  props: {
    market: {},
    kind: {
      type: String,
      default: 'modal',
    },
    closeJourneyAfterSuccess: {
      type: Boolean,
      default: true,
    },
    showRelatedContracts: {
      type: Boolean,
      default: false,
    },
    showContractCharts: {
      type: Boolean,
      default: false,
    },
    showOrderSlip: {
      type: Boolean,
      default: true,
    },
    disablePurchaseOfferWatchers: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('trade', ['tradeOptions', 'tradeSuccess', 'confirmationData', 'confirmationDoneData']),
    ...mapGetters('app', ['isLayoutMobile', 'isLayoutDesktop']),
    ...mapState('user', ['status']),
    showPurchaseOfferDesktop() {
      return this.kind === 'desktop' && !!this.contract;
    },
    showPurchaseOfferMobile() {
      return this.kind === 'mobile' && !!this.contract;
    },
    showPurchaseOfferDesktopWidget() {
      return this.kind === 'desktop-widget' && !!this.contract;
    },
    showPurchaseOfferMobileWidget() {
      return this.kind === 'mobile-widget' && !!this.contract;
    },
    minimumOwnership() {
      return Math.max(get(this.contract, 'minimumClipSize', 1), 1);
    },
    contract() {
      if (!this.tradeOptions.contractId) return null;
      const contracts = this.market.contracts || this.market.marketContracts;
      return contracts.find(c => c.contractId === this.tradeOptions.contractId);
    },
    payload() {
      return {
        quantity: this.tradeOptions.quantity,
        pricePerShare: this.tradeOptions.pricePerShare,
        contractId: this.tradeOptions.contractId,
        tradeType: this.tradeType,
      };
    },
    tradeType() {
      switch (`${this.tradeOptions.operationType}_${this.tradeOptions.operationValue}`) {
        case 'buy_no': return 0;
        case 'buy_yes': return 1;
        case 'sell_no': return 2;
        case 'sell_yes': return 3;
        default: return -1;
      }
    },
  },
  data() {
    return {
      hasInitialised: false,
    };
  },
  watch: {
    tradeOptions: {
      handler: 'tradeOptionsHandler',
      immediate: true,
    },
    tradeSuccess(value) {
      if (value) {
        this.setCurrentPopup();
        // desktop exclusive
        if (this.closeJourneyAfterSuccess) {
          this.setTradeOptions(null);
        }

        this.getMarketContracts(this.market.marketId)
          .then(() => {
            log.info('[PURCHASE] successful');
            this.setTradeSuccess(false);
            const contractModel = new Contract(this.contract);
            // switch to buy yes, user got rid of ownership
            if (this.tradeOptions.operationType === OPERATIONS.SELL && !contractModel.mode) {
              log.info('[PURCHASE] user lost ownership after selling');
              this.setTradeOptions({
                contractId: this.contract.contractId,
                operationType: OPERATIONS.BUY,
                operationValue: VALUES.YES,
              });
            }
          });
      }
    },
  },
  methods: {
    ...mapActions('profile', ['clearTradedMarkets']),
    ...mapActions('market', ['getContract', 'setMarketEngineBusyMessage', 'getMarketContracts']),
    ...mapActions('trade', [
      'submitTrade', 'confirmTrade', 'setTradeOptions', 'setTradeQuantity',
      'setTradeSuccess', 'setIsInConfirmationMode', 'setConfirmationData',
      'setConfirmationDoneData', 'setBottomBetSlipExtendedMode'
    ]),
    ...mapActions('notifications', ['addSimple']),
    ...mapActions('popup', ['setCurrentPopup', 'setTradePopup', 'setCurrentPopupOptions']),
    getErrorFromResponse(response) {
      if (!response) return '';
      if (typeof response.data === 'string') {
        return response.data;
      }
      return (response.data) ? response.data.message : '';
    },
    tradeOptionsHandler(tradeOptions, oldOptions = {}) {
      if (oldOptions.contractId !== tradeOptions.contractId) {
        this.setConfirmationData(null);
        this.setConfirmationDoneData(null);
      }
      if (
        oldOptions.operationValue !== tradeOptions.operationValue ||
        oldOptions.operationType !== tradeOptions.operationType
      ) {
        this.setConfirmationData(null);
        this.setConfirmationDoneData(null);
      }
    },
    onClickSubmitWithoutSkipConfirmationHandling() {
      this.processTrade({ canSkip: false });
    },
    onClickNext() {
      this.processTrade({ canSkip: true });
    },
    processTrade({ canSkip = true } = {}) {
      if (this.isLoading) {
        return;
      }

      if (!this.isLoggedIn) {
        this.setCurrentPopup(MODALS.LOGIN);
        return;
      }

      if (this.isLoggedIn && !this.status.hasBeenFunded) {
        this.showAddFundsPopup({ delay: false });
        return;
      }

      if (!this.isUserVerified) {
        this.showAccountVerifyPopup();
        return;
      }

      if (canSkip && this.tradeOptions.skipConfirmation) {
        this.submitTradeAction();
      } else {
        this.loaderAdd(document.body);
        this.confirmTrade(this.payload)
          .then((response) => {
            this.loaderRemove(document.body);
            // when quantity is reduced it has to be set
            if (response.data.quantity && response.data.quantity >= this.minimumOwnership) {
              this.setTradeQuantity(response.data.quantity);
            }
            if (this.isLayoutDesktop && this.currentPopup !== MODALS.PURCHASE) {
              this.setTradePopup({
                name: MODALS.PURCHASE_SUSHI_REVIEW,
                options: {
                  market: this.market,
                },
              });
            }
            if (this.isLayoutMobile && this.currentPopup !== MODALS.PURCHASE) {
              this.setTradePopup({
                name: MODALS.PURCHASE_SUSHI_REVIEW,
                options: {
                  market: this.market,
                },
              });
            }
            this.setConfirmationData(response.data);
            this.setConfirmationDoneData(null);
            this.setIsInConfirmationMode(true);
          })
          .catch((error) => {
            this.inspectErrorForRestrictions(error);
            this.loaderRemove(document.body);
            const message = this.getErrorFromResponse(error.response);
            if (message) {
              this.addSimple({
                title: 'Error Occurred',
                icon: 'cancel',
                text: message,
              });
            }
          });
      }
    },
    submitTradeAction() {
      this.loaderAdd(document.body);
      this.submitTrade(this.payload)
        .then((response) => {
          const data = response.data;
          this.$gtm.trackEvent({ event: 'Trade' });
          this.loaderRemove(document.body);
          this.setConfirmationData(null);
          this.setConfirmationDoneData(data);
          this.addSimple({
            externalId: (data.offer) ? (data.offer.offerId) : null,
            closeTimeout: 3500,
            icon: 'pending',
            text: 'You will be notified when processing is complete.',
            title: 'Order Received',
          });
          this.setMarketEngineBusyMessage({
            isEngineBusy: data.isEngineBusy,
            isEngineBusyMessage: data.isEngineBusyMessage,
          });
          this.setTradeSuccess(true);
          this.setBottomBetSlipExtendedMode(false);
          this.clearTradedMarkets();
          const isBuyTradeType = [0, 1].includes(this.tradeType);
          notificationProxy.showMessagingJourney(isBuyTradeType);
        })
        .catch((error) => {
          this.inspectErrorForRestrictions(error);
          this.setTradeSuccess(false);
          this.loaderRemove(document.body);
          const message = this.getErrorFromResponse(error.response);
          if (message) {
            this.addSimple({
              title: 'Error Occurred',
              icon: 'cancel',
              text: message,
            });
          }
        });
    },
    onClickSubmit() {
      this.submitTradeAction();
    },
    onClickAmend() {
      this.setConfirmationData(null);
      this.setConfirmationDoneData(null);
      this.setIsInConfirmationMode(false);
    },
    inspectErrorForRestrictions(error) {
      const restrictionPreamble = get(error, 'response.data.restrictionPreamble', null);
      const restrictions = get(error, 'response.data.restrictions', null);
      if (restrictionPreamble && restrictions) {
        this.setCurrentPopup(MODALS.MARKET_OWNERSHIP_CONFLICT);
        this.setCurrentPopupOptions({
          restrictions,
          restrictionPreamble,
        });
      }
    },
  },
  beforeDestroy() {
    this.setIsInConfirmationMode(false);
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/purchase-journey-sushi/purchase-journey-sushi.js